import React, { useContext } from 'react';
import {
	IStyleSettingsCarouselUploaderContext,
	StyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import { ICustomItemsOfGroups } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { useTranslation } from 'react-i18next';
import { useCarouselItemUrl } from '../useCarouselItemUrl';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import Text from 'components/Text/Text';

interface IProps {
	item: ICustomItemsOfGroups;
	isSelected: boolean;
	isMakeRequest: boolean;
	onSelect: () => void;
	onDelete: () => void;
}

export const CarouselThumbNew: React.FC<IProps> = ({ item, isSelected, isMakeRequest, onSelect, onDelete }) => {
	const { t } = useTranslation('form');
	const { changeTypeOfErrorModalWindow } = useContext<IStyleSettingsCarouselUploaderContext>(
		StyleSettingsCarouselUploaderContext,
	);
	const { url, isError, showError } = useCarouselItemUrl({ item, photoSize: 'Small', isMakeRequest });
	const isVideo = item.mediaFileType === 2;
	const imageExtension = item.filePath?.split('.').pop() === 'gif' ? 'gif' : 'png';
	const downloadFile = useBlobDownload({
		filePath: item.filePath,
		fileExtension: isVideo ? 'mp4' : imageExtension,
		fileName: isVideo ? 'styleVideo' : 'styleImage',
		onError: () => changeTypeOfErrorModalWindow('fileDownloadError'),
	});

	const renderMedia = () => {
		if (isError) {
			return (
				<div className="h-full flex items-center justify-center bg-purple-01-new" data-test="ThumbError">
					<Text
						translationNameSpace="form"
						translationKey={isVideo ? 'videoLoadError' : 'imageLoadError'}
						theme="neutralMedium"
						size="xs"
					/>
				</div>
			);
		}

		if (!url) {
			return <LoaderWithProps loaderSize="small" />;
		}

		return isVideo ? (
			<video className="w-full h-full block object-cover" src={url} muted={true} onError={showError} />
		) : (
			<img className="w-full h-full block object-cover" src={url} alt="Photo" onError={showError} />
		);
	};

	return (
		<div className="relative h-40 overflow-hidden" data-test="CarouselThumb">
			{renderMedia()}
			{isVideo && url && !isError && (
				<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
					<svg className="w-10 h-10 text-white-00-new" fill="currentColor">
						<use xlinkHref={`${breadcrumbs}#playBtn`} />
					</svg>
				</div>
			)}
			{!isSelected && (
				<div
					className="absolute inset-0 bg-grey-10-new opacity-40 cursor-pointer"
					data-test="SelectThumb"
					onClick={onSelect}
				/>
			)}
			{isSelected && item.filePath && (
				<div
					className="absolute inset-0 flex items-center justify-center bg-grey-10-new opacity-0 cursor-pointer transition-opacity hover:opacity-60"
					data-test="DownloadItem"
					onClick={downloadFile}>
					<Icon name={IconName.download} size="xxl" className="text-white-00-new" />
				</div>
			)}
			<div className="absolute top-2 right-2">
				<NewButton
					variant="shadow"
					color="red"
					size="small"
					icon={true}
					iconLeft={<Icon name={IconName.delete} size="lg" />}
					ariaLabel={t('deleteButton')}
					testId="deleteItemButton"
					onClick={onDelete}
				/>
			</div>
		</div>
	);
};
